import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { LobFilter, LobItem } from '../../../../models/api/reports/lob-filter';
import { ReportsService } from '../../../../services/reports/reports.service';
import { BaseComponent } from '../../../base/base.component';
import { SelectFieldComponent } from '../../../core/fields/select-field.component';
import { FilterSection, ICriteria } from '../edit-filter.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-lob-criteria',
  imports: [CommonModule, SelectFieldComponent],
  template: `
    <ng-template #select let-item>
      <div [class]="item.classes">
        <app-select-field
          [label]="item.label"
          [control]="item.control"
          optionLabel="description"
          optionValue="value"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="item.items"
          [showHelpText]="false"
          [showToggleAll]="item.showToggleAll"
          selectAllLabel="All"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [disabled]="item.disabled"
          [loading]="loading"
        />
      </div>
    </ng-template>

    <div class="tw-pt-2">
      <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'Line of business',
                control: fg.controls.lineOfBusiness,
                items: availableLobs,
                showToggleAll: false,
                labelClasses: '',
                disabled: false,
              },
            }
          "
        ></ng-container>

        <h4 class="tw-font-bold tw-text-lg tw-py-2 tw-border-b tw-m-0">
          Claim type
        </h4>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'Workers’ compensation',
                control: fg.controls.claimTypeWc,
                items: wcClaimTypes,
                showToggleAll: true,
                disabled: !isLobSelected('WC'),
              },
            }
          "
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'Disability',
                control: fg.controls.claimTypeDs,
                items: dsClaimTypes,
                showToggleAll: true,
                disabled: !isLobSelected('DS'),
              },
            }
          "
        ></ng-container>

        <h4 class="tw-font-bold tw-text-lg tw-py-2 tw-border-b tw-m-0">Coverage</h4>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'Auto liability',
                control: fg.controls.coverageAu,
                items: auCoverages,
                showToggleAll: true,
                disabled: !isLobSelected('AU'),
              },
            }
          "
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'General liability',
                control: fg.controls.coverageGl,
                items: glCoverages,
                showToggleAll: true,
                disabled: !isLobSelected('GL'),
              },
            }
          "
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'Property',
                control: fg.controls.coveragePr,
                items: prCoverages,
                showToggleAll: true,
                disabled: !isLobSelected('PR'),
              },
            }
          "
        ></ng-container>
      </div>
    </div>
  `,
  styles: ``,
})
export class LobCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  @Input()
  section: FilterSection;

  availableLobs: ListItem[] = [
    { value: 'WC', description: 'Workers’ compensation' },
    { value: 'DS', description: 'Disability' },
    { value: 'AU', description: 'Auto liability' },
    { value: 'GL', description: 'General liability' },
    { value: 'PR', description: 'Property' },
  ];
  wcClaimTypes: LobItem[] = [];
  dsClaimTypes: LobItem[] = [];
  glCoverages: LobItem[] = [];
  auCoverages: LobItem[] = [];
  prCoverages: LobItem[] = [];

  loading = true;

  fg = new FormGroup({
    lineOfBusiness: new FormControl<string[]>([]),
    claimTypeWc: new FormControl<string[]>([]),
    claimTypeDs: new FormControl<string[]>([]),
    coverageGl: new FormControl<string[]>([]),
    coverageAu: new FormControl<string[]>([]),
    coveragePr: new FormControl<string[]>([]),
  });

  private lobData: LobFilter | null = null;
  private previousLobs: string[] = [];

  private reports = inject(ReportsService);

  constructor() {
    super();
  }

  setLobFromCriteria(criteria: FilterCriteria[]) {
    const lobs = criteria
      .filter((c) => c.column === 'LINE_OF_BUSINESS')
      .flatMap((c) => c.values);

    const claimTypesWc = criteria
      .filter(
        (c) =>
          c.column === 'CLAIM_TYPE_AND_SUBTYPE' && c.lineOfBusiness === 'WC',
      )
      .flatMap((c) => c.values);

    const claimTypesDs = criteria
      .filter(
        (c) =>
          c.column === 'CLAIM_TYPE_AND_SUBTYPE' && c.lineOfBusiness === 'DS',
      )
      .flatMap((c) => c.values);

    const coverageCodesGl = criteria
      .filter(
        (c) =>
          c.column === 'LINE_CODE_AND_COVERAGE_CODE' &&
          c.lineOfBusiness === 'GL',
      )
      .flatMap((c) => c.values);

    const coverageCodesAu = criteria
      .filter(
        (c) =>
          c.column === 'LINE_CODE_AND_COVERAGE_CODE' &&
          c.lineOfBusiness === 'AU',
      )
      .flatMap((c) => c.values);

    const coverageCodesPr = criteria
      .filter(
        (c) =>
          c.column === 'LINE_CODE_AND_COVERAGE_CODE' &&
          c.lineOfBusiness === 'PR',
      )
      .flatMap((c) => c.values);

    if (lobs.length) {
      this.fg.controls.lineOfBusiness.setValue(lobs);
    }

    if (claimTypesWc.length) {
      this.fg.controls.claimTypeWc.setValue(claimTypesWc, { emitEvent: false });
    }

    if (claimTypesDs.length) {
      this.fg.controls.claimTypeDs.setValue(claimTypesDs, { emitEvent: false });
    }

    if (coverageCodesGl.length) {
      this.fg.controls.coverageGl.setValue(coverageCodesGl, {
        emitEvent: false,
      });
    }

    if (coverageCodesAu.length) {
      this.fg.controls.coverageAu.setValue(coverageCodesAu, {
        emitEvent: false,
      });
    }

    if (coverageCodesPr.length) {
      this.fg.controls.coveragePr.setValue(coverageCodesPr, {
        emitEvent: false,
      });
    }

    this.emitCount();
  }

  ngOnInit() {
    this.section.criteria = this;
    this.dispose.subs = this.fg.controls.lineOfBusiness.valueChanges.subscribe(
      (newLobs) => {
        this.updateLobDependentFields(newLobs || [], this.previousLobs);
        this.previousLobs = [...(newLobs || [])];
      },
    );

    this.dispose.subs = this.fg.valueChanges.subscribe(() => this.emitCount());

    const client = this.reports.selectedClient();
    if (client) {
      this.loadLobData(client);
    }
  }

  @HandleLoadingState()
  private async loadLobData(client: string) {
    this.lobData = await this.api.filtersCriteria.getLobFilter(client);
    this.getLobDisplayItems(this.availableLobs);

    this.wcClaimTypes = this.lobData.wcClaimTypes;
    this.dsClaimTypes = this.lobData.dsClaimTypes;
    this.glCoverages = this.lobData.glCoverages;
    this.auCoverages = this.lobData.auCoverages;
    this.prCoverages = this.lobData.prCoverages;

    this.fg.reset({
      lineOfBusiness: [],
      claimTypeWc: [],
      claimTypeDs: [],
      coverageGl: [],
      coverageAu: [],
      coveragePr: [],
    });

    this.emitCount();

    if (this.section.values?.length) {
      this.setLobFromCriteria(this.section.values);
    }
  }

  private getLobDisplayItems(lobs: ListItem[]): ListItem[] {
    const lobLabels: Record<string, string> = {
      WC: "Workers' Compensation",
      DS: 'Disability',
      AU: 'Auto Liability',
      GL: 'General Liability',
      PR: 'Property',
    };

    return lobs.map((lob) => ({
      value: lob.value,
      description: lobLabels[lob.value] || lob.description,
    }));
  }

  isLobSelected(lob: string): boolean {
    const selected = this.fg.controls.lineOfBusiness.value || [];
    return selected.includes(lob);
  }

  private updateLobDependentFields(
    currentLobs: string[],
    previousLobs: string[],
  ) {
    if (currentLobs.includes('WC')) {
      if (!previousLobs.includes('WC')) {
        this.fg.controls.claimTypeWc.setValue(
          this.wcClaimTypes.map((t) => t.value),
        );
      }
    } else {
      this.fg.controls.claimTypeWc.setValue([]);
    }

    if (currentLobs.includes('DS')) {
      if (!previousLobs.includes('DS')) {
        this.fg.controls.claimTypeDs.setValue(
          this.dsClaimTypes.map((t) => t.value),
        );
      }
    } else {
      this.fg.controls.claimTypeDs.setValue([]);
    }

    if (currentLobs.includes('GL')) {
      if (!previousLobs.includes('GL')) {
        this.fg.controls.coverageGl.setValue(
          this.glCoverages.map((c) => c.value),
        );
      }
    } else {
      this.fg.controls.coverageGl.setValue([]);
    }

    if (currentLobs.includes('AU')) {
      if (!previousLobs.includes('AU')) {
        this.fg.controls.coverageAu.setValue(
          this.auCoverages.map((c) => c.value),
        );
      }
    } else {
      this.fg.controls.coverageAu.setValue([]);
    }

    if (currentLobs.includes('PR')) {
      if (!previousLobs.includes('PR')) {
        this.fg.controls.coveragePr.setValue(
          this.prCoverages.map((c) => c.value),
        );
      }
    } else {
      this.fg.controls.coveragePr.setValue([]);
    }
  }

  valid(): boolean {
    return true;
  }

  getCriteria(): FilterCriteria[] {
    return [
      ...this.getLobCriteria(),
      ...this.getClaimTypeCriteria(),
      ...this.getCoverageCriteria(),
    ];
  }

  private getLobCriteria(): FilterCriteria[] {
    const selectedLobs = this.fg.controls.lineOfBusiness.value || [];
    if (selectedLobs.length === 0) {
      return [];
    }

    return [
      ({
        tableAlias: 'claim',
        column: 'LINE_OF_BUSINESS',
        displayName: '',
        operator: 'EQUALS',
        values: selectedLobs,
        dataType: '',
        formattedOperator: '',
        formattedValue: '',
        filterSection: 'Line of business',
        filterSubsection: '',
        lineOfBusiness: '',
        isClaimLab: false,
      } as FilterCriteria),
    ];
  }

  private getClaimTypeCriteria(): FilterCriteria[] {
    const wcValues = this.fg.controls.claimTypeWc.value || [];
    const dsValues = this.fg.controls.claimTypeDs.value || [];

    return [
      ...this.mapClaimTypesToCriteria(wcValues, 'WC', this.wcClaimTypes.length),
      ...this.mapClaimTypesToCriteria(dsValues, 'DS', this.dsClaimTypes.length),
    ];
  }

  private mapClaimTypesToCriteria(
    values: string[],
    lob: string,
    totalOptions: number,
  ): FilterCriteria[] {
    if (values.length === 0 || values.length === totalOptions) return [];

    const allItems = values.filter((v) => !v.includes('|'));
    const individualItems = values.filter((v) => v.includes('|'));

    return [
      ...(allItems.length > 0
        ? [this.createClaimTypeCriteria(allItems, lob, 'BEGINS')]
        : []),
      ...(individualItems.length > 0
        ? [this.createClaimTypeCriteria(individualItems, lob, 'EQUALS')]
        : []),
    ];
  }

  private createClaimTypeCriteria(
    values: string[],
    lob: string,
    operator: 'BEGINS' | 'EQUALS',
  ): FilterCriteria {
    return {
      tableAlias: 'claim',
      column: 'CLAIM_TYPE_AND_SUBTYPE',
      displayName: '',
      operator,
      values,
      dataType: '',
      formattedOperator: '',
      formattedValue: '',
      filterSection: 'Line of business',
      filterSubsection: '',
      lineOfBusiness: lob,
      isClaimLab: false,
    } as FilterCriteria;
  }

  private getCoverageCriteria(): FilterCriteria[] {
    const coverages = [
      {
        values: this.fg.controls.coverageGl.value || [],
        lob: 'GL',
        total: this.glCoverages.length,
      },
      {
        values: this.fg.controls.coverageAu.value || [],
        lob: 'AU',
        total: this.auCoverages.length,
      },
      {
        values: this.fg.controls.coveragePr.value || [],
        lob: 'PR',
        total: this.prCoverages.length,
      },
    ];

    return coverages
      .filter((c) => c.values.length > 0 && c.values.length !== c.total)
      .map((c) => this.createCoverageCriteria(c.values, c.lob));
  }

  private createCoverageCriteria(
    values: string[],
    lob: string,
  ): FilterCriteria {
    return {
      tableAlias: 'claim',
      column: 'LINE_CODE_AND_COVERAGE_CODE',
      displayName: '',
      operator: 'EQUALS',
      values,
      dataType: '',
      formattedOperator: '',
      formattedValue: '',
      filterSection: 'Line of business',
      filterSubsection: '',
      lineOfBusiness: lob,
      isClaimLab: false,
    } as FilterCriteria;
  }

  private emitCount() {
    if (!this.section) return;

    let count = 0;
    const lobs = this.fg.controls.lineOfBusiness.value || [];
    count += lobs.length;
    this.section.count.set(count);
  }
}
